import React, { memo, useMemo } from "react";
import clsx from "clsx";
import { useFormContext } from "react-hook-form";

import { Button } from "components";
import { agentPaymentsService, merchantPaymentsService } from "services";
import { BillToTypes } from "types";

import AchPaymentSection from "./achPaymentSection";
import BillingStatusField from "./billingStatusField";
import BillTo from "./billTo";
import PaymentMethodsList from "./paymentMethodsList";
import PaymentType from "./paymentType";

import styles from "../../terminalSolution.module.scss";

type PaymentMethodProps = {
	isLoading: boolean;
	index: number;
	sectionName: string;
	data: { [key: string]: any };
	merchantInfo: { [key: string]: any };
	removePayment: Function;
};

// This component is used only on merchant, tid.
const PaymentMethod = ({
	data,
	index,
	sectionName,
	isLoading,
	removePayment,
	merchantInfo,
}: PaymentMethodProps) => {
	const { watch } = useFormContext();

	const isFirstKey = index === 0;
	const billToType = watch(`${sectionName}.${index}.billToInfo.billTo`);
	const isBillToAurora = billToType === BillToTypes.aurora.value?.toString();
	const isBillToAgent = Number(billToType) === BillToTypes.agent.value;
	const submitService = useMemo(
		() =>
			isBillToAgent
				? agentPaymentsService.addPaymentMethod
				: merchantPaymentsService.addPaymentMethod,
		[isBillToAgent],
	);

	const entityId = useMemo(
		() => (isBillToAgent ? merchantInfo?.agent?.id : merchantInfo?.id),
		[isBillToAgent, merchantInfo?.agent?.id, merchantInfo?.id],
	);

	return (
		<>
			{!isFirstKey && (
				<>
					<hr className={clsx(styles.divider, "mt-3 mb-4")} />
					<div className="d-flex justify-content-end">
						<Button
							value="Remove Payee"
							minWidth="110px"
							className="p-0 text-left"
							style={{
								height: " 25px",
								minHeight: "25px",
							}}
							variant="secondary"
							onClick={() => removePayment(index)}
						/>
					</div>
				</>
			)}

			<BillTo
				isLoading={isLoading}
				sectionName={sectionName}
				billToName={`${sectionName}.${index}.billToInfo.billTo`}
				amountToBeBilledName={`${sectionName}.${index}.billToInfo.amountToBeBilled`}
				index={index}
				defaultValue={data?.billToInfo}
			/>
			{isBillToAurora ? (
				<BillingStatusField
					isLoading={isLoading}
					sectionName={sectionName}
					index={index}
					defaultValue={data?.paymentTypeInfo}
				/>
			) : (
				<>
					<PaymentType
						isLoading={isLoading}
						sectionName={sectionName}
						index={index}
						paymentTypeName={`${sectionName}.${index}.paymentTypeInfo.paymentType`}
					/>
					<AchPaymentSection
						isLoading={isLoading}
						sectionName={sectionName}
						index={index}
						defaultValue={data?.paymentTypeInfo}
					/>

					<PaymentMethodsList
						primaryAgentId={merchantInfo?.agent?.id}
						merchantId={merchantInfo?.id}
						billToName={`${sectionName}.${index}.billToInfo.billTo`}
						paymentTypeName={`${sectionName}.${index}.paymentTypeInfo.paymentType`}
						paymentMethodName={`${sectionName}.${index}.paymentMethod`}
						paymentMethodChosenId={
							data?.paymentMethodChosenId || ""
						}
						submitService={submitService}
						entityId={entityId}
						paymentMethodKey={data?.id}
						merchantData={merchantInfo}
						isLoading={isLoading}
					/>
				</>
			)}
		</>
	);
};

export default memo(PaymentMethod);
